import { Link } from "react-router-dom";
import { Mail, Phone, MapPin } from "lucide-react";

const Footer = () => {
  return (
    <footer role="contentinfo">

      {/* Silhouette SVG — fill must match footer bg */}
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 1440 180"
        className="block w-full"
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        
        {/* Mid layer: tall pine/fir trees */}
        <g fill="#1e1642" opacity="0.75">
          <polygon points="140,180 170,90 200,180"/><polygon points="148,145 170,75 192,145"/><polygon points="155,118 170,55 185,118"/><rect x="166" y="170" width="8" height="10"/>
          <polygon points="270,180 305,82 340,180"/><polygon points="279,142 305,66 331,142"/><polygon points="287,112 305,48 323,112"/><rect x="301" y="170" width="8" height="10"/>
          <polygon points="430,180 462,88 494,180"/><polygon points="438,144 462,72 486,144"/><polygon points="446,114 462,52 478,114"/><rect x="458" y="170" width="8" height="10"/>
          <polygon points="590,180 628,78 666,180"/><polygon points="600,138 628,60 656,138"/><polygon points="610,105 628,42 646,105"/><rect x="624" y="170" width="8" height="10"/>
          <polygon points="750,180 785,85 820,180"/><polygon points="759,143 785,68 811,143"/><polygon points="768,112 785,50 802,112"/><rect x="781" y="170" width="8" height="10"/>
          <polygon points="910,180 948,80 986,180"/><polygon points="920,140 948,62 976,140"/><polygon points="930,108 948,44 966,108"/><rect x="944" y="170" width="8" height="10"/>
          <polygon points="1070,180 1105,88 1140,180"/><polygon points="1079,146 1105,70 1131,146"/><polygon points="1088,114 1105,52 1122,114"/><rect x="1101" y="170" width="8" height="10"/>
          <polygon points="1240,180 1275,84 1310,180"/><polygon points="1249,142 1275,66 1301,142"/><polygon points="1258,110 1275,48 1292,110"/><rect x="1271" y="170" width="8" height="10"/>
        </g>

        {/* Front layer: dense foreground firs */}
        <g fill="#1e1642">
          <polygon points="0,180 22,130 44,180"/><polygon points="6,162 22,118 38,162"/>
          <polygon points="38,180 58,140 78,180"/><polygon points="43,165 58,128 73,165"/>
          <polygon points="72,180 96,125 120,180"/><polygon points="78,160 96,112 114,160"/>
          <polygon points="150,180 175,120 200,180"/><polygon points="156,158 175,106 194,158"/>
          <polygon points="232,180 252,130 272,180"/><polygon points="238,160 252,116 266,160"/>
          <polygon points="268,180 290,122 312,180"/><polygon points="274,158 290,108 306,158"/>
          <polygon points="340,180 362,118 384,180"/><polygon points="346,156 362,104 378,156"/>
          <polygon points="416,180 440,115 464,180"/><polygon points="422,155 440,100 458,155"/>
          <polygon points="496,180 518,120 540,180"/><polygon points="502,158 518,106 534,158"/>
          <polygon points="572,180 596,118 620,180"/><polygon points="578,156 596,104 614,156"/>
          <polygon points="652,180 675,116 698,180"/><polygon points="658,154 675,102 692,154"/>
          <polygon points="730,180 754,122 778,180"/><polygon points="736,158 754,108 772,158"/>
          <polygon points="810,180 834,115 858,180"/><polygon points="816,154 834,100 852,154"/>
          <polygon points="890,180 912,118 934,180"/><polygon points="896,156 912,104 928,156"/>
          <polygon points="966,180 990,120 1014,180"/><polygon points="972,157 990,106 1008,157"/>
          <polygon points="1046,180 1070,116 1094,180"/><polygon points="1052,155 1070,102 1088,155"/>
          <polygon points="1126,180 1150,118 1174,180"/><polygon points="1132,157 1150,104 1168,157"/>
          <polygon points="1206,180 1228,120 1250,180"/><polygon points="1212,158 1228,106 1244,158"/>
          <polygon points="1282,180 1306,116 1330,180"/><polygon points="1288,155 1306,102 1324,155"/>
          <polygon points="1362,180 1386,120 1410,180"/><polygon points="1368,158 1386,106 1404,158"/>
          <polygon points="1408,180 1424,145 1440,180"/>
          <rect x="0" y="175" width="1440" height="5"/>
        </g>
      </svg>

      {/* Footer body */}
      <div className="bg-[#1e1642] text-white">
        <div className="mx-auto max-w-7xl px-4 py-14">
          <div className="grid gap-10 md:grid-cols-3">

            <div>
              <h3 className="text-2xl font-extrabold tracking-tight text-violet-300">
                Northstart Systems
              </h3>
              <p className="mt-3 max-w-sm text-sm leading-relaxed text-white/60">
                Empowering digital transformation through reliable ICT solutions,
                built for real teams, real workflows, and real impact.
              </p>
            </div>

            <nav aria-label="Footer Services">
              <h4 className="text-xl font-bold text-violet-300">Services</h4>
              <ul className="mt-4 space-y-3">
                {[
                  { to: "/service/immigration-services", label: "Immigration, Visa & Work Permit" },
                  { to: "/service/development", label: "Development Solutions" },
                  { to: "/service/e-procurement", label: "E-Procurement Solutions" },
                  { to: "/service/security-solutions", label: "Security Solutions" },
                  { to: "/service/ict-systems-support", label: "ICT Systems & Support" },
                  { to: "/service/consultancy", label: "Consultancy Services" },
                ].map((item) => (
                  <li key={item.to}>
                    <Link
                      to={item.to}
                      className="group inline-flex items-center gap-2 text-white/60 transition hover:text-white"
                    >
                      <span className="h-1.5 w-1.5 rounded-full bg-indigo-400 opacity-70 group-hover:opacity-100" />
                      <span className="text-sm">{item.label}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>

            <address className="not-italic">
              <h4 className="text-xl font-bold text-violet-300">Contact</h4>
              <div className="mt-4 space-y-4 text-white/60">
                <div className="flex items-start gap-3">
                  <MapPin className="mt-1 h-5 w-5 shrink-0 text-indigo-400" />
                  <div className="text-sm leading-relaxed">
                    <p>Riara Valley Garden, Apartment C8, 3rd Floor</p>
                    <p>Off Riara Road, Kilimani, Nairobi, Kenya</p>
                  </div>
                </div>
                <a href="mailto:info@northstartsystems.com" className="flex items-center gap-3 text-sm transition hover:text-white">
                  <Mail className="h-5 w-5 shrink-0 text-indigo-400" />
                  info@northstartsystems.com
                </a>
                <a href="tel:+254700041385" className="flex items-center gap-3 text-sm transition hover:text-white">
                  <Phone className="h-5 w-5 shrink-0 text-indigo-400" />
                  +254 700 041 385
                </a>
              </div>
            </address>
          </div>

          <div className="mt-12 border-t border-white/10 pt-8 text-center">
            <p className="text-sm text-white/40 tracking-wide">
              2026 Northstart Systems. All rights reserved.
            </p>
            <div className="mt-4 flex flex-col items-center gap-2">
              {/* <span
                className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-indigo-600 to-purple-600 text-sm font-extrabold text-white"
                aria-hidden="true"
              >
                SC
              </span> */}
              <span className="text-sm text-white/30">
                Designed by SonnieCodes
              </span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;